import { useState, useEffect } from "react"
import {
  Stack,
  Text,
  Image,
  AspectRatio,
  TextInput,
  Center,
  ActionIcon,
  Textarea,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"
import { IconPhotoOff, IconTrash, IconX } from "@tabler/icons-react"

import { CancelButton, SaveButton, SubmitButton } from "@components/Buttons"
import { FormButtonGroup } from "@components/form-button-group"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { useChatPostAttachmentApi } from "@/attachment-api/chatPostAttachmentApi"
import PickAndCropImageModal from "@/routes/app/-components/pick-and-crop-image-modal"
import {
  useCreateChatPost,
  useUpdateMessageContent,
  getGetFeedPostsQueryKey,
  getGetFeedPostsForUserQueryKey,
} from "@/orval/gen"
import { ChatPostDto } from "@/orval/gen/model"
import { DefaultModal, DefaultModalProps } from "@components/default-modal"
import { isEmptyOrWhitespace } from "@/utils/isEmptyOrWhitespace"
import { TextEditor } from "@/routes/app/-components/TextEditor"
import { stripHtml } from "@/utils/stripHtml"

interface Props extends DefaultModalProps {
  chatId: number
  mode: "create" | "edit"
  chatPostDto?: ChatPostDto
  isComment?: boolean
}

export function FeedPostFormModal({
  chatId,
  mode,
  chatPostDto,
  isComment = false,
  ...props
}: Props) {
  const { t } = useTranslation("app_server")
  const queryClient = useQueryClient()

  const [isPublishOrDraftAction, setIsPublishOrDraftAction] = useState(false)

  const isEdit = mode === "edit" && chatPostDto

  const { mutateAsync: createPost, isPending: isCreating } = useCreateChatPost()
  const { mutateAsync: updatePost, isPending: isUpdating } =
    useUpdateMessageContent()
  const isPending = isCreating || isUpdating

  const { getFileUrl, deleteAttachmentAndMetadata, addAttachmentAndMetadata } =
    useChatPostAttachmentApi()

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      title: isEdit ? (isComment ? null : chatPostDto.title) : "",
      message: isEdit ? chatPostDto.postText : "",
    },
  })

  const [file, setFile] = useState<File | null>(null)
  const [showPickModal, setShowPickModal] = useState(false)

  const existingAttachment = chatPostDto?.attachments?.at(0)
  const [attachmentUrl, setAttachmentUrl] = useState<string>()
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    if (isEdit && existingAttachment) {
      const url = getFileUrl(
        existingAttachment.attachmentId,
        chatPostDto.chatPostId
      )
      setAttachmentUrl(url)
    }
  }, [existingAttachment, isEdit])

  const isMessageEmpty = isEmptyOrWhitespace(
    stripHtml(form.getValues().message)
  )

  const invalidateFeedQueries = async () => {
    try {
      await queryClient.invalidateQueries({
        queryKey: getGetFeedPostsQueryKey(),
      })
      await queryClient.invalidateQueries({
        queryKey: getGetFeedPostsForUserQueryKey(),
      })
    } catch (err) {
      console.error("Failed to invalidate feed queries", err)
    }
  }

  const handleSubmit = async (formValues: typeof form.values) => {
    try {
      if (!isEdit) {
        const newPostId = await createPost({
          data: {
            chatId,
            title: formValues.title ?? "",
            message: formValues.message,
            replyToMessageId: null,
            isDraft: isPublishOrDraftAction,
          },
        })
        await invalidateFeedQueries()

        if (file) {
          await addAttachmentAndMetadata(
            file,
            { chatPostId: newPostId },
            newPostId
          )
        }
      } else {
        await updatePost({
          data: {
            chatPostId: chatPostDto.chatPostId,
            newMessage: formValues.message,
            title: formValues.title,
            publish: isPublishOrDraftAction,
          },
        })
        await invalidateFeedQueries()

        if (existingAttachment && (file || !attachmentUrl)) {
          await deleteAttachmentAndMetadata(
            existingAttachment.attachmentId,
            chatPostDto.chatPostId
          )
        }
        if (file) {
          await addAttachmentAndMetadata(
            file,
            { chatPostId: chatPostDto.chatPostId },
            chatPostDto.chatPostId
          )
        }
      }

      props.onClose()
    } catch (err) {
      showErrorNotification(err as Error)
    }
  }

  const getModalTitle = () => {
    if (isComment) return t("EditComment")
    if (isEdit) return chatPostDto.isDraft ? t("EditDraft") : t("EditPost")
    return t("NewPost")
  }

  return (
    <DefaultModal title={getModalTitle()} {...props} size="lg">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack onClick={(evt) => evt.stopPropagation()}>
          {!isComment ? (
            <>
              <TextInput
                required
                maxLength={150}
                label={t("Title")}
                key={form.key("title")}
                {...form.getInputProps("title")}
              />

              <AspectRatio
                ratio={16 / 9}
                onClick={() => setShowPickModal(true)}
                style={{ cursor: "pointer" }}
                pos={"relative"}
                onDragOver={(e) => {
                  e.preventDefault()
                  setIsDragging(true)
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault()
                  setIsDragging(false)
                  const f = e.dataTransfer?.files?.[0]
                  if (f) {
                    if (!f.type || !f.type.startsWith("image/")) {
                      showErrorNotification(new Error(t("InvalidFile")))
                      return
                    }
                    setFile(f)
                  }
                }}
              >
                {(file || attachmentUrl) && (
                  <ActionIcon
                    pos={"absolute"}
                    style={{ zIndex: 9999 }}
                    top={4}
                    right={4}
                    w={"auto"}
                    variant="subtle"
                    color="red"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (file) setFile(null)
                      if (!file && existingAttachment)
                        setAttachmentUrl(undefined)
                    }}
                  >
                    <IconTrash />
                  </ActionIcon>
                )}
                {isDragging && (
                  <Center
                    pos={"absolute"}
                    top={0}
                    left={0}
                    w={"100%"}
                    h={"100%"}
                    style={{
                      background:
                        "color-mix(in srgb, var(--mantine-color-gray-6) 60%, transparent)",
                      zIndex: 9998,
                      pointerEvents: "none",
                    }}
                  >
                    <Text color="white">{t("DragAttachmentsHere")}</Text>
                  </Center>
                )}
                {file ? (
                  <Image
                    fit="contain"
                    w="100%"
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                  />
                ) : attachmentUrl ? (
                  <Image fit="contain" w="100%" src={attachmentUrl} />
                ) : (
                  <Center>
                    <IconPhotoOff />
                    <Text>{t("CoverImage")}</Text>
                  </Center>
                )}
              </AspectRatio>

              <PickAndCropImageModal
                opened={showPickModal}
                onClose={() => setShowPickModal(false)}
                initialImageUrl={attachmentUrl ?? null}
                aspect={16 / 9}
                onUpload={(f: File) => {
                  setFile(f)
                }}
                onDelete={() => {
                  setFile(null)
                  setAttachmentUrl(undefined)
                }}
              />

              <TextEditor
                showEmojiPicker={!isEdit}
                {...form.getInputProps("message")}
                value={form.getValues().message}
                key={form.key("message")}
              />
            </>
          ) : (
            <Textarea
              autosize
              minRows={3}
              {...form.getInputProps("message")}
              value={form.getValues().message}
              key={form.key("message")}
            />
          )}

          <FormButtonGroup>
            <CancelButton onClick={props.onClose} disabled={isPending} />

            {!isEdit ? (
              <>
                <SubmitButton
                  variant="outline"
                  color="blue"
                  loading={isPending && isPublishOrDraftAction}
                  disabled={isPending || isMessageEmpty}
                  onClick={() => setIsPublishOrDraftAction(true)}
                >
                  {t("SaveDraft")}
                </SubmitButton>
                <SubmitButton
                  loading={isPending && !isPublishOrDraftAction}
                  disabled={isPending || isMessageEmpty}
                  onClick={() => setIsPublishOrDraftAction(false)}
                >
                  {t("PublishPost")}
                </SubmitButton>
              </>
            ) : (
              <>
                <SaveButton
                  variant="outline"
                  color="blue"
                  loading={isPending && !isPublishOrDraftAction}
                  disabled={isPending}
                  onClick={() => setIsPublishOrDraftAction(false)}
                />
                {chatPostDto.isDraft && (
                  <SubmitButton
                    variant="filled"
                    color="blue"
                    loading={isPending && isPublishOrDraftAction}
                    disabled={isPending}
                    onClick={() => setIsPublishOrDraftAction(true)}
                  >
                    {t("Publish")}
                  </SubmitButton>
                )}
              </>
            )}
          </FormButtonGroup>
        </Stack>
      </form>
    </DefaultModal>
  )
}
